import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getMercadoById } from '../services/firebase';
import Loading from './common/Loading';
import logo from '../assets/ma2.png';
import styles from '../styles/mkt.module.css';
import Confetti from 'react-confetti';  // ← IMPORTAR CONFETI

// ===== ICONOS =====
import { 
  FaMapMarkerAlt, 
  FaCalendarAlt,
  FaWifi,
  FaRestroom,
  FaTree,
  FaParking,
  FaWheelchair,
  FaAmbulance,
  FaTruck,
  FaTag,
  FaDoorOpen,
  FaChevronDown,
  FaChevronUp,
  FaInstagram,
  FaWhatsapp,
  FaBookOpen
} from 'react-icons/fa';

const Mkt = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mercado, setMercado] = useState(null);
  const [error, setError] = useState(null);
  const [historiaAbierta, setHistoriaAbierta] = useState(false);
  const [mostrarConfeti, setMostrarConfeti] = useState(false);
  const [dimensiones, setDimensiones] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const cargarMercado = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getMercadoById(id);
        if (!data) {
          setError('Mercado no encontrado');
          setLoading(false);
          return;
        }
        setMercado(data);
        
        // Verificar si hoy es el día del festejo
        if (data.fechaFestejo) {
          const hoy = new Date();
          const diaHoy = hoy.getDate().toString().padStart(2, '0');
          const mesHoy = (hoy.getMonth() + 1).toString().padStart(2, '0');
          const fechaHoy = `${diaHoy}-${mesHoy}`;
          
          if (fechaHoy === data.fechaFestejo) {
            setMostrarConfeti(true);
            // Detener confeti después de 8 segundos
            setTimeout(() => setMostrarConfeti(false), 8000);
          }
        }
        
      } catch (error) {
        console.error('Error cargando mercado:', error);
        setError('Error al cargar los datos. Intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    };
    if (id) cargarMercado();
  }, [id]);

  // Actualizar dimensiones al redimensionar
  useEffect(() => {
    const handleResize = () => {
      setDimensiones({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getDiaActual = () => {
    const dias = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sábado'];
    return dias[new Date().getDay()];
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const [dia, mes] = fecha.split('-');
    const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    return `${parseInt(dia)} de ${meses[parseInt(mes) - 1]}`;
  };

  const ordenDias = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
  const diasSemana = {
    lunes: 'Lunes',
    martes: 'Martes',
    miercoles: 'Miércoles',
    jueves: 'Jueves',
    viernes: 'Viernes',
    sabado: 'Sábado',
    domingo: 'Domingo'
  };

  const amenidadesIcons = {
    wifi: { icon: FaWifi, label: 'WiFi gratis' },
    banos: { icon: FaRestroom, label: 'Baños públicos' },
    areaVerde: { icon: FaTree, label: 'Área verde' },
    estacionamiento: { icon: FaParking, label: 'Estacionamiento' },
    accesibilidad: { icon: FaWheelchair, label: 'Accesibilidad' },
    atencionMedica: { icon: FaAmbulance, label: 'Atención médica' },
    enviosDomicilio: { icon: FaTruck, label: 'Envíos a domicilio' }
  };

  const openGoogleMaps = () => {
    if (!mercado?.geolocalizacion?.lat || !mercado?.geolocalizacion?.lng) return;
    const { lat, lng } = mercado.geolocalizacion;
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  const toggleHistoria = () => setHistoriaAbierta(!historiaAbierta);

  if (loading) return <Loading message="Cargando mercado..." />;

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>{error}</p>
        <button onClick={() => window.location.reload()} className={styles.reloadButton}>
          🔄 Intentar de nuevo
        </button>
      </div>
    );
  }

  if (!mercado) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorMessage}>Mercado no encontrado</p>
        <button onClick={() => navigate('/')} className={styles.reloadButton}>
          ← Volver al inicio
        </button>
      </div>
    );
  }

  return (
    <div className={styles.mktContainer}>

      {/* CONFETI - Solo se muestra si es día de festejo */}
      {mostrarConfeti && (
        <div className={styles.confetiWrapper}>
          <Confetti
            width={dimensiones.width}
            height={dimensiones.height}
            numberOfPieces={200}
            colors={['#43115E', '#C23D6B', '#F2C14E', '#5BA566', '#E67E22', '#FF6B6B', '#4ECDC4']}
            recycle={false}
            gravity={0.15}
            wind={0.02}
          />
          <div className={styles.festejoBanner}>
            🎉 ¡Hoy es el día de festejo de {mercado.nombre}! 🎉
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className={styles.homeHeader}>
        <div className={styles.homeHeaderContainer}>
          <Link to="/" className={styles.homeLogo}>
            <img
              src={logo}
              alt="Mercado Abierto"
              className={styles.homeLogoImg}
            />
          </Link>
        </div>
      </header>

      {/* ===== CONTENIDO PRINCIPAL ===== */}
      <main className={styles.mktMainContent}>

        {/* ===== HERO ===== */}
        <section 
          className={styles.hero}
          style={{ backgroundImage: `url(${mercado.portada || '/img/default-market.jpg'})` }}
        >
          <div className={styles.heroOverlay}>
            <div className={styles.heroContent}>
              <h1>{mercado.nombre}</h1>
              <p>Productos frescos, comercio local y tradición.</p>

              <div className={styles.heroInfo}>
                {mercado.geolocalizacion?.lat && (
                  <div className={styles.infoItem}>
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Ubicación</strong>
                      <button className={styles.mapButton} onClick={openGoogleMaps}>
                        Ver en Google Maps ↗
                      </button>
                    </div>
                  </div>
                )}
                {mercado.fechaFestejo && (
                  <div className={styles.infoItem}>
                    <FaCalendarAlt />
                    <div>
                      <strong>Fiesta tradicional</strong>
                      <span>{formatearFecha(mercado.fechaFestejo)}</span>
                      {/* Badge de "Hoy es el día" si coincide */}
                      {(() => {
                        const hoy = new Date();
                        const diaHoy = hoy.getDate().toString().padStart(2, '0');
                        const mesHoy = (hoy.getMonth() + 1).toString().padStart(2, '0');
                        const fechaHoy = `${diaHoy}-${mesHoy}`;
                        if (fechaHoy === mercado.fechaFestejo) {
                          return <span className={styles.festejoBadge}>🎉 ¡HOY!</span>;
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                )}
              </div>

              <button 
                className={styles.enterButton} 
                onClick={() => navigate(`/search/${id}`)}
              >
                <FaDoorOpen />
                Entrar al Mercado →
              </button>
            </div>
          </div>
        </section>

        {/* ===== HISTORIA ===== */}
        <section className={styles.card}>
          <button className={styles.historiaButton} onClick={toggleHistoria}>
            <div className={styles.historiaIconWrap}>
              <FaBookOpen size={22} />
            </div>
            <span>Historia del Mercado</span>
            {historiaAbierta ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />}
          </button>
          {historiaAbierta && (
            <div className={styles.historiaContent}>
              {mercado.descripcion || 'Sin descripción disponible.'}
            </div>
          )}
        </section>

        {/* ===== HORARIOS ===== */}
        <section className={styles.card}>
          <h2>🕐 Horarios</h2>
          <div className={styles.mktHorarios}>
            {mercado.horarios ? (
              ordenDias.map((dia) => {
                const horario = mercado.horarios[dia];
                if (!horario) return null;
                const esHoy = dia === getDiaActual().toLowerCase();
                return (
                  <div key={dia} className={`${styles.horarioItem} ${esHoy ? styles.hoy : ''}`}>
                    <strong>{diasSemana[dia]}</strong>
                    {esHoy && <span className={styles.hoyBadge}>Hoy</span>}
                    <p>{horario.abierto ? `${horario.apertura} – ${horario.cierre}` : 'Cerrado'}</p>
                  </div>
                );
              })
            ) : (
              <p className={styles.noData}>Sin horarios disponibles.</p>
            )}
          </div>
        </section>

        {/* ===== SECCIONES ===== */}
        {mercado.secciones?.length > 0 && (
          <section className={styles.card}>
            <h2>🏷️ Secciones (Pasillos)</h2>
            <div className={styles.mktSecciones}>
              {mercado.secciones.map((item, index) => (
                <div key={index} className={styles.seccionTag}>
                  <FaTag size={14} />
                  {item}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ===== SERVICIOS ===== */}
        {mercado.servicios?.length > 0 && (
          <section className={styles.card}>
            <h2>📋 Servicios adicionales</h2>
            <div className={styles.mktServicios}>
              {mercado.servicios.map((item, index) => (
                <div key={index} className={styles.servicioTag}>
                  {item}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ===== AMENIDADES ===== */}
        {mercado.amenidades && (
          <section className={styles.card}>
            <h2>⭐ Amenidades</h2>
            <div className={styles.mktAmenidades}>
              {Object.entries(mercado.amenidades).map(([key, value]) => {
                if (!value) return null;
                const amenidad = amenidadesIcons[key];
                if (!amenidad) return null;
                const Icon = amenidad.icon;
                return (
                  <div key={key} className={styles.amenidadItem}>
                    <Icon size={26} />
                    <span>{amenidad.label}</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ===== CTA FINAL ===== */}
        <section className={styles.bottomCTA}>
          <div>
            <h3>Apoya lo local, fortalece tu comunidad</h3>
            <p>Descubre más de nuestros mercados en Morelia.</p>
          </div>
          <button onClick={() => navigate('/')}>
            Explorar más mercados →
          </button>
        </section>

      </main>

      {/* ===== FOOTER ===== */}
      <footer className={styles.mktFooter}>
        <div className={styles.mktFooterGrid}>
          <div>
            <img src={logo} alt="logo" className={styles.mktFooterLogo} />
            <p>Plataforma digital que conecta a los mercados municipales de México contigo.</p>
          </div>
          <div>
            <h4>Enlaces rápidos</h4>
            <p onClick={() => navigate('/')}>Inicio</p>
            <p onClick={() => navigate('/')}>Explorar mercados</p>
            <p onClick={() => navigate('/privacidad')}>Aviso de Privacidad</p>
            <p onClick={() => navigate('/terminos')}>Términos y Condiciones</p>
          </div>
          <div>
            <h4>Síguenos</h4>
            <div className={styles.socials}>
              <FaInstagram size={28} />
              <FaWhatsapp size={28} />
            </div>
          </div>
        </div>
        <div className={styles.mapFooterBottom}>
            © {new Date().getFullYear()} mercadoabierto.online - Todos los derechos reservados
        </div>
      </footer>

    </div>
  );
};

export default Mkt;